/**
 * useAnalyticsAgentTools — wires the CRM Analytics page into FutureBot.
 *
 * The AnalyticsPage monolith passes its entire relevant state + stable setters
 * as one object per render (snapshotted into a ref here), and this hook
 * implements:
 *   - get_analytics_state / set_analytics_filters / set_active_tab
 *   - get_analytics_data with client-side shapers that reduce huge deal-level
 *     arrays (stagnant leads, no-followup, Monday-report deals) into LLM-safe
 *     aggregates (counts + breakdowns + worst offenders)
 *   - run_analytics_query: an OFF-SCREEN queryCrmAnalyticsV2 call with
 *     overridden filters for period-over-period comparisons. It never touches
 *     page state — the user's screen stays exactly as-is.
 *
 * Tool names must match functions/FutureBot/toolsets.js (crm-analytics).
 */

import { useCallback, useMemo, useRef } from 'react';
import { httpsCallable } from 'firebase/functions';
import { functions } from '../../../utils/init-firebase';
import { waitForIdle } from '../../../components/FutureBot/agentToolHelpers';

const TAB_NAMES = ['Overview', 'User Activity', 'Task Health', 'Lead Health', 'Monday Report', 'Campaigns'];
const NO_TAGS_VALUE = '__NO_TAGS__';

const ANALYTICS_SUGGESTIONS = [
  'Analyze what I\'m looking at — what stands out?',
  'How does this period compare to the previous one?',
  'Which lead sources actually convert?',
];

// Which batch report feeds each section (mirrors the page's TAB_TO_BATCHES).
const SECTION_TO_BATCH = {
  summary: 'batch_overview',
  funnel: 'batch_overview',
  sources: 'batch_overview',
  lo_performance: 'batch_overview',
  user_activity: 'batch_activity',
  task_analytics: 'batch_activity',
  task_health: 'batch_activity',
  activity_summary: 'batch_activity',
  speed_to_lead: 'batch_health',
  appointments_apps: 'batch_health',
  stagnant_summary: 'batch_health',
  no_followup_summary: 'batch_health',
  monday_totals: 'monday_report',
  monday_by_user: 'monday_report',
  deal_detail_summary: 'monday_report',
  campaign_summary: 'batch_campaigns',
  campaign_breakdown: 'batch_campaigns',
};

const MAX_PASSTHROUGH_ROWS = 60;
const round1 = (n) => (Number.isFinite(n) ? Math.round(n * 10) / 10 : null);

function countBy(rows, keyFn) {
  const counts = {};
  rows.forEach((row) => {
    const key = keyFn(row) || 'unknown';
    counts[key] = (counts[key] || 0) + 1;
  });
  return counts;
}

function topEntries(counts, limit = 15) {
  return Object.entries(counts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([key, count]) => ({ key, count }));
}

/** Stagnant-leads list (can be 100s-1000s of rows) → aggregate summary. */
export function shapeStagnantSummary(rows) {
  const list = Array.isArray(rows) ? rows : [];
  const days = list.map((r) => Number(r.days_stagnant)).filter(Number.isFinite);
  return {
    totalStagnant: list.length,
    avgDaysStagnant: round1(days.reduce((a, b) => a + b, 0) / (days.length || 1)),
    byStage: countBy(list, (r) => r.stage),
    byOwner: topEntries(countBy(list, (r) => r.owner_name)),
    worstOffenders: [...list]
      .sort((a, b) => (Number(b.days_stagnant) || 0) - (Number(a.days_stagnant) || 0))
      .slice(0, 10)
      .map((r) => ({
        dealName: r.dealName || r.deal_name,
        stage: r.stage,
        owner: r.owner_name,
        daysStagnant: Number(r.days_stagnant) || 0,
        thresholdDays: Number(r.threshold_days) || undefined,
      })),
  };
}

/** Leads-without-followup list → aggregate summary. */
export function shapeNoFollowupSummary(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return {
    totalWithoutFollowup: list.length,
    byStage: countBy(list, (r) => r.stage),
    byOwner: topEntries(countBy(list, (r) => r.owner_name)),
    examples: list.slice(0, 10).map((r) => ({
      dealName: r.dealName || r.deal_name,
      stage: r.stage,
      owner: r.owner_name,
    })),
  };
}

/** Monday-report deal-level rows (up to thousands) → aggregate summary. */
export function shapeDealDetailSummary(deals) {
  const list = Array.isArray(deals) ? deals : [];
  const stl = list.map((d) => Number(d.stl_biz_minutes)).filter(Number.isFinite).sort((a, b) => a - b);
  const dispositionTotals = {};
  list.forEach((d) => {
    Object.entries(d.disposition_counts || {}).forEach(([k, v]) => {
      dispositionTotals[k] = (dispositionTotals[k] || 0) + (Number(v) || 0);
    });
  });

  const bySourceCounts = {};
  list.forEach((d) => {
    const key = d.source_medium || 'unknown';
    if (!bySourceCounts[key]) bySourceCounts[key] = { leads: 0, contacted: 0, withConversation: 0 };
    bySourceCounts[key].leads += 1;
    if (d.contacted === 'Yes') bySourceCounts[key].contacted += 1;
    if (d.conversation === 'Yes') bySourceCounts[key].withConversation += 1;
  });
  const bySource = Object.entries(bySourceCounts)
    .sort((a, b) => b[1].leads - a[1].leads)
    .slice(0, 15)
    .map(([source, stats]) => ({
      source,
      leads: stats.leads,
      contactedPct: round1((stats.contacted / stats.leads) * 100),
      conversationPct: round1((stats.withConversation / stats.leads) * 100),
    }));

  const byOwnerCounts = {};
  list.forEach((d) => {
    const key = d.owner_name || 'unknown';
    if (!byOwnerCounts[key]) byOwnerCounts[key] = { leads: 0, contacted: 0, withConversation: 0, stl: [] };
    byOwnerCounts[key].leads += 1;
    if (d.contacted === 'Yes') byOwnerCounts[key].contacted += 1;
    if (d.conversation === 'Yes') byOwnerCounts[key].withConversation += 1;
    const minutes = Number(d.stl_biz_minutes);
    if (Number.isFinite(minutes)) byOwnerCounts[key].stl.push(minutes);
  });
  const byOwner = Object.entries(byOwnerCounts)
    .sort((a, b) => b[1].leads - a[1].leads)
    .slice(0, 15)
    .map(([owner, stats]) => ({
      owner,
      leads: stats.leads,
      contactedPct: round1((stats.contacted / stats.leads) * 100),
      conversationPct: round1((stats.withConversation / stats.leads) * 100),
      avgStlBizMinutes: stats.stl.length ? round1(stats.stl.reduce((a, b) => a + b, 0) / stats.stl.length) : null,
    }));

  return {
    totalLeads: list.length,
    uncontacted: list.filter((d) => d.contacted !== 'Yes').length,
    medianStlBizMinutes: stl.length ? round1(stl[Math.floor(stl.length / 2)]) : null,
    byStage: countBy(list, (d) => d.stage),
    bySource,
    byOwner,
    dispositionTotals,
  };
}

const capRows = (rows) => (Array.isArray(rows) ? rows.slice(0, MAX_PASSTHROUGH_ROWS) : []);

/** Per-campaign rows → slim LLM-safe rows (top 40 by enrollments in range). */
export function shapeCampaignBreakdown(rows) {
  const list = Array.isArray(rows) ? rows : [];
  return [...list]
    .sort((a, b) => (Number(b.enrolled) || 0) - (Number(a.enrolled) || 0))
    .slice(0, 40)
    .map((c) => ({
      name: c.name,
      category: c.category,
      trigger: c.trigger_type,
      active: !!c.is_active,
      runners: `${Number(c.active_subscribers) || 0}/${Number(c.total_subscribers) || 0}`,
      enrolled: Number(c.enrolled) || 0,
      activeNow: Number(c.active_now) || 0,
      completed: Number(c.completed) || 0,
      failed: Number(c.failed) || 0,
      successRatePct: round1(Number(c.success_rate)),
      emailsSent: Number(c.emails_sent) || 0,
      openRatePct: round1(Number(c.open_rate)),
      clickRatePct: round1(Number(c.click_rate)),
      smsSent: Number(c.sms_sent) || 0,
      fundedDeals: Number(c.funded_deals) || 0,
      fundedVolume: Number(c.funded_volume) || 0,
    }));
}

/** Shape one section from a raw batch response (run_analytics_query path). */
function sectionFromBatchData(section, data) {
  const d = data || {};
  switch (section) {
    case 'summary': return (Array.isArray(d.summary) ? d.summary[0] : d.summary) || {};
    case 'funnel': return capRows(d.funnel);
    case 'sources': return capRows(d.volume);
    case 'lo_performance': return capRows(d.loPerformance);
    case 'user_activity': return capRows(d.userActivity);
    case 'task_analytics': return capRows(d.taskAnalytics);
    case 'task_health': return (Array.isArray(d.taskHealth) ? d.taskHealth[0] : d.taskHealth) || {};
    case 'activity_summary': return (Array.isArray(d.activitySummary) ? d.activitySummary[0] : d.activitySummary) || {};
    case 'speed_to_lead': return capRows(d.speedToLead);
    case 'appointments_apps': return capRows(d.appointmentsApps);
    case 'stagnant_summary': return shapeStagnantSummary(d.stagnantLeads);
    case 'no_followup_summary': return shapeNoFollowupSummary(d.leadsWithoutFollowup);
    case 'monday_totals': return d.totals || {};
    case 'monday_by_user': return capRows(d.byUser);
    case 'deal_detail_summary': return shapeDealDetailSummary(d.deals);
    case 'campaign_summary': return d.summary || {};
    case 'campaign_breakdown': return shapeCampaignBreakdown(d.campaigns);
    default: return null;
  }
}

export function useAnalyticsAgentTools(pageApi, { enabled = true } = {}) {
  const apiRef = useRef(pageApi);
  apiRef.current = pageApi;
  const loadingRef = useRef(false);
  loadingRef.current = !!pageApi.loading;

  /** Shape one section from the LIVE page state (get_analytics_data path). */
  const sectionFromScreen = useCallback((section) => {
    const api = apiRef.current;
    switch (section) {
      case 'summary': return api.summary || {};
      case 'funnel': return capRows(api.funnelData);
      case 'sources': return capRows(api.volumeData);
      case 'lo_performance': return capRows(api.loStats);
      case 'user_activity': return capRows(api.userActivityData);
      case 'task_analytics': return capRows(api.taskAnalyticsData);
      case 'task_health': return api.taskHealthSummary || {};
      case 'activity_summary': return api.activitySummary || {};
      case 'speed_to_lead': return capRows(api.speedToLeadData);
      case 'appointments_apps': return capRows(api.appointmentsAppsData);
      case 'stagnant_summary': return shapeStagnantSummary(api.stagnantLeads);
      case 'no_followup_summary': return shapeNoFollowupSummary(api.leadsWithoutFollowup);
      case 'monday_totals': return api.mondayReportData?.totals || {};
      case 'monday_by_user': return capRows(api.mondayReportData?.byUser);
      case 'deal_detail_summary': return shapeDealDetailSummary(api.mondayReportData?.deals);
      case 'campaign_summary': return api.campaignAnalytics?.summary || {};
      case 'campaign_breakdown': return shapeCampaignBreakdown(api.campaignAnalytics?.campaigns);
      default: return null;
    }
  }, []);

  const describeFilters = useCallback(() => {
    const api = apiRef.current;
    const { start, end } = api.getDateRange();
    return {
      dateRangeCode: api.dateRangeCode,
      dateRange: { start, end },
      ...(api.dateRangeCode === 'custom'
        ? { customStartDate: api.customStartDate, customEndDate: api.customEndDate }
        : {}),
      users: api.committedUsers.length
        ? api.committedUsers.map((u) => ({ id: u.value, name: u.label }))
        : 'all (whole team)',
      stages: api.committedStages.length ? api.committedStages.map((s) => s.value) : 'all',
      sources: api.committedSourceMediums.length ? api.committedSourceMediums.map((s) => s.value) : 'all',
      tags: api.committedTags.length ? api.committedTags.map((t) => t.value) : 'all',
    };
  }, []);

  /**
   * Match requested values against a {value,label} option list (accepts either
   * the value or the label, case-insensitive). Returns { options } or { error }.
   */
  const matchOptions = useCallback((requested, optionList, kind) => {
    const matched = [];
    const unknown = [];
    for (const raw of requested) {
      const wanted = String(raw).trim().toLowerCase();
      const option = optionList.find(
        (o) => String(o.value).toLowerCase() === wanted || String(o.label || '').toLowerCase() === wanted
      );
      if (option) matched.push(option);
      else unknown.push(raw);
    }
    if (unknown.length) {
      const valid = optionList.slice(0, 40).map((o) => o.value).join(', ');
      return { error: `Unknown ${kind}: ${unknown.join(', ')}. Valid values: ${valid}${optionList.length > 40 ? ', …' : ''}.` };
    }
    return { options: matched };
  }, []);

  // -------------------------------------------------------------------------
  // Tools
  // -------------------------------------------------------------------------

  const tools = useMemo(() => ({
    get_analytics_state: () => {
      const api = apiRef.current;
      return {
        filters: describeFilters(),
        activeTab: { index: api.tabIndex, name: TAB_NAMES[api.tabIndex] },
        loading: !!api.loading,
        error: api.error || null,
        loadedDatasets: api.loadedBatches,
        isAdmin: !!api.isCRMAdmin,
        headlineKPIs: api.summary || {},
      };
    },

    set_analytics_filters: async (args) => {
      const api = apiRef.current;
      const a = args || {};
      const updates = [];

      if (a.users !== undefined) {
        if (!api.isCRMAdmin) {
          return { error: 'Only CRM admins can filter by user here — this user\'s analytics are already scoped to themselves.' };
        }
        if (a.users.length === 0) {
          api.setCommittedUsers([]);
          updates.push('users: all');
        } else {
          const { options, error } = matchOptions(a.users, api.users, 'user(s)');
          if (error) return { error };
          api.setCommittedUsers(options);
          updates.push(`users: ${options.map((o) => o.label).join(', ')}`);
        }
      }
      if (a.stages !== undefined) {
        if (a.stages.length === 0) {
          api.setCommittedStages([]);
          updates.push('stages: all');
        } else {
          const { options, error } = matchOptions(a.stages, api.stageOptions, 'stage(s)');
          if (error) return { error };
          api.setCommittedStages(options);
          updates.push(`stages: ${options.map((o) => o.value).join(', ')}`);
        }
      }
      if (a.sourceMediums !== undefined) {
        if (a.sourceMediums.length === 0) {
          api.setCommittedSourceMediums([]);
          updates.push('sources: all');
        } else {
          const { options, error } = matchOptions(a.sourceMediums, api.sourceOptions, 'source(s)');
          if (error) return { error };
          api.setCommittedSourceMediums(options);
          updates.push(`sources: ${options.map((o) => o.value).join(', ')}`);
        }
      }
      if (a.tags !== undefined) {
        if (a.tags.length === 0) {
          api.setCommittedTags([]);
          updates.push('tags: all');
        } else {
          const { options, error } = matchOptions(a.tags, api.tagOptions, 'tag(s)');
          if (error) return { error };
          api.setCommittedTags(options);
          updates.push(`tags: ${options.map((o) => o.value).join(', ')}`);
        }
      }
      if (a.customStartDate) api.setCustomStartDate(String(a.customStartDate).slice(0, 10));
      if (a.customEndDate) api.setCustomEndDate(String(a.customEndDate).slice(0, 10));
      if (a.dateRangeCode) {
        api.setDateRangeCode(a.dateRangeCode);
        updates.push(`date range: ${a.dateRangeCode}`);
      }

      if (updates.length === 0 && !a.customStartDate && !a.customEndDate) {
        return { error: 'No changes given — pass dateRangeCode, users, stages, sourceMediums, tags, or custom dates.' };
      }

      // Wait for the refetch to settle so the numbers we report are fresh.
      await waitForIdle(loadingRef, { timeoutMs: 25000 });
      return {
        ok: true,
        applied: updates,
        filters: describeFilters(),
        headlineKPIs: apiRef.current.summary || {},
      };
    },

    set_active_tab: async (args) => {
      const idx = Number(args?.tabIndex);
      if (!Number.isInteger(idx) || idx < 0 || idx > 5) {
        return { error: 'tabIndex must be 0-5.' };
      }
      apiRef.current.setTabIndex(idx);
      await waitForIdle(loadingRef, { timeoutMs: 25000 });
      return {
        ok: true,
        tab: { index: idx, name: TAB_NAMES[idx] },
        loadedDatasets: apiRef.current.loadedBatches,
      };
    },

    get_analytics_data: (args) => {
      const api = apiRef.current;
      const sections = Array.isArray(args?.sections) ? args.sections : [];
      const result = {};
      const notLoaded = [];
      for (const section of sections) {
        const batch = SECTION_TO_BATCH[section];
        result[section] = sectionFromScreen(section);
        if (batch && !api.loadedBatches.includes(batch)) notLoaded.push(section);
      }
      if (notLoaded.length) {
        result.note =
          `Sections not loaded yet (their tab hasn't been opened): ${notLoaded.join(', ')}. ` +
          'Their data above may be empty — call set_active_tab for the tab that needs them, then re-read.';
      }
      return result;
    },

    run_analytics_query: async (args) => {
      const api = apiRef.current;
      const a = args || {};
      const sections = Array.isArray(a.sections) && a.sections.length ? a.sections : ['summary'];

      // Resolve date range: overrides win, otherwise the committed state.
      const code = a.dateRangeCode || api.dateRangeCode;
      const startOverride = a.customStartDate ? String(a.customStartDate).slice(0, 10) : api.customStartDate;
      const endOverride = a.customEndDate ? String(a.customEndDate).slice(0, 10) : api.customEndDate;
      const { start, end } = api.computeDateRange(code, startOverride, endOverride);

      // Resolve filters: overrides win, otherwise committed values.
      let userIds = api.committedUsers.map((u) => u.value);
      if (a.users !== undefined) {
        if (!api.isCRMAdmin) {
          return { error: 'Only CRM admins can query other users\' analytics.' };
        }
        if (a.users.length === 0) userIds = [];
        else {
          const { options, error } = matchOptions(a.users, api.users, 'user(s)');
          if (error) return { error };
          userIds = options.map((o) => o.value);
        }
      }
      const resolveValues = (override, committed, optionList, kind) => {
        if (override === undefined) return { values: committed.map((o) => o.value) };
        if (override.length === 0) return { values: [] };
        const { options, error } = matchOptions(override, optionList, kind);
        return error ? { error } : { values: options.map((o) => o.value) };
      };
      const stagesRes = resolveValues(a.stages, api.committedStages, api.stageOptions, 'stage(s)');
      if (stagesRes.error) return { error: stagesRes.error };
      const sourcesRes = resolveValues(a.sourceMediums, api.committedSourceMediums, api.sourceOptions, 'source(s)');
      if (sourcesRes.error) return { error: sourcesRes.error };
      // Tags allow the __NO_TAGS__ sentinel even if it's not in the options list.
      let tagValues = api.committedTags.map((t) => t.value);
      if (a.tags !== undefined) {
        if (a.tags.length === 0) tagValues = [];
        else {
          const sentinel = a.tags.filter((t) => t === NO_TAGS_VALUE);
          const named = a.tags.filter((t) => t !== NO_TAGS_VALUE);
          let namedValues = [];
          if (named.length) {
            const { options, error } = matchOptions(named, api.tagOptions, 'tag(s)');
            if (error) return { error };
            namedValues = options.map((o) => o.value);
          }
          tagValues = [...sentinel, ...namedValues];
        }
      }

      const payload = {
        dateRange: { start, end },
        filters: { userIds, stages: stagesRes.values, sourceMediums: sourcesRes.values, tags: tagValues },
      };

      const batches = [...new Set(sections.map((s) => SECTION_TO_BATCH[s]).filter(Boolean))];
      try {
        const queryFn = httpsCallable(functions, 'queryCrmAnalyticsV2', { timeout: 120000 });
        const responses = await Promise.all(
          batches.map((batch) => queryFn({ ...payload, reportType: batch }).then((res) => ({ batch, data: res.data.data })))
        );
        const byBatch = Object.fromEntries(responses.map((r) => [r.batch, r.data]));
        const result = {
          note: 'OFF-SCREEN comparison query — the user\'s on-screen view is unchanged.',
          dateRange: { start, end, dateRangeCode: code },
          filters: payload.filters,
          sections: {},
        };
        for (const section of sections) {
          result.sections[section] = sectionFromBatchData(section, byBatch[SECTION_TO_BATCH[section]]);
        }
        return result;
      } catch (err) {
        return { error: `Comparison query failed: ${err.message}` };
      }
    },
  }), [describeFilters, matchOptions, sectionFromScreen]);

  // -------------------------------------------------------------------------
  // Page context for the system prompt
  // -------------------------------------------------------------------------

  const getContext = useCallback(async () => {
    const api = apiRef.current;
    const filters = describeFilters();
    const referenceData = {
      stages: api.stageOptions.map((s) => ({ value: s.value, label: s.label })),
      sources: api.sourceOptions.slice(0, 80).map((s) => s.value),
      tags: api.tagOptions.map((t) => t.value),
    };
    if (api.isCRMAdmin) {
      referenceData.users = api.users.map((u) => ({ id: u.value, name: u.label }));
    }
    const filterBits = [
      `date range ${filters.dateRangeCode}`,
      filters.users === 'all (whole team)' ? 'all users' : `${api.committedUsers.length} user(s)`,
      filters.stages === 'all' ? 'all stages' : `${api.committedStages.length} stage(s)`,
      filters.sources === 'all' ? 'all sources' : `${api.committedSourceMediums.length} source(s)`,
      filters.tags === 'all' ? 'all tags' : `${api.committedTags.length} tag(s)`,
    ];
    return {
      referenceData,
      stateSummary:
        `Analytics on the ${TAB_NAMES[api.tabIndex]} tab — ${filterBits.join(', ')}` +
        `${api.loading ? ' (data currently refreshing)' : ''}.`,
    };
  }, [describeFilters]);

  const registration = useMemo(() => {
    if (!enabled) return null;
    return {
      pageId: 'crm-analytics',
      title: 'CRM Analytics',
      tools,
      getContext,
      suggestions: ANALYTICS_SUGGESTIONS,
    };
  }, [enabled, tools, getContext]);

  return { registration };
}

export default useAnalyticsAgentTools;
